import React, { useState } from "react";
import {
    Collapse as MuiCollapse, Menu as MuiMenu, MenuItem as MuiMenuItem,
    InputBase as MuiInputBase
} from "@mui/material";
import { Check, ChevronDown, ChevronRight, ChevronUp, Plus } from "lucide-react";
import type { IntegrationLinkDto } from "@/api/integrations/links";
import type { ProviderState, SourceRow } from "./useIntegrationsData";
import { connectedTargetRows, LinkTable, linkKey } from "./LinkTable";

interface Props {
    state: ProviderState;
    productNameById: ReadonlyMap<string, string> | ReadonlyMap<number, string>;
    contextMeshNameById?: ReadonlyMap<string, string>;
    showProducts?: boolean;
    showContextMeshes?: boolean;
    syncingIds: ReadonlySet<string | number>;
    pendingUnlinkKey?: string | null;
    pendingUnlinkId?: number | null;
    collapsed: boolean;
    linking: boolean;
    resyncing: boolean;
    onConnect: (configValue: string) => void;
    onLink?: () => void;
    onLinkProduct?: () => void;
    onToggleCollapsed: () => void;
    onManageResync: () => void;
    onRetryListing: () => void;
    onManageReconnect: () => void;
    onManageDisconnect: () => void;
    onSync: (row: SourceRow, link: IntegrationLinkDto) => void;
    onUnlink: (row: SourceRow, link: IntegrationLinkDto) => void;
}

const cardStyle: React.CSSProperties = {
    border: "1px solid var(--border)", borderRadius: 13, background: "var(--bg-paper)",
    overflow: "hidden", marginBottom: 14, boxShadow: "0 1px 2px rgba(15,23,42,0.04)"
};

function Tile({ state, size }: { state: ProviderState; size: number }) {
    return (
        <span style={{
            width: size, height: size, borderRadius: 9, background: state.meta.tile.bg,
            border: `1px solid ${state.meta.tile.border}`, display: "flex",
            alignItems: "center", justifyContent: "center", color: state.meta.tile.fg, flex: "0 0 auto"
        }}>
            {state.meta.logo}
        </span>
    );
}

function ConnectedCard(props: Props) {
    const { state, collapsed } = props;
    const [manageAnchor, setManageAnchor] = useState<HTMLElement | null>(null);
    const legacyProductOnly = props.showProducts === undefined && props.showContextMeshes === undefined;
    const showProducts = props.showProducts ?? true;
    const showContextMeshes = props.showContextMeshes ?? false;
    const productCount = connectedTargetRows(state.rows, "product").length;
    const contextMeshCount = connectedTargetRows(state.rows, "context_mesh").length;
    const count = productCount + contextMeshCount;
    const hasSyncInProgress = state.rows.some(row =>
        row.links.some(link => props.syncingIds.has(linkKey(link)) || props.syncingIds.has(link.id)));

    return (
        <div style={cardStyle}>
            <div style={{
                padding: "16px 20px", display: "flex", alignItems: "center", gap: 14,
                borderBottom: collapsed ? "none" : "1px solid var(--divider)", background: "var(--bg-panel)"
            }}>
                <Tile state={state} size={38} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 15.5, fontWeight: 600, color: "var(--fg1)" }}>{state.meta.name}</span>
                        <span style={{
                            display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                            color: "var(--success)", background: "#ecfdf5", border: "1px solid #a7f3d0",
                            padding: "2px 9px", borderRadius: 999
                        }}>
                            <Check size={11} strokeWidth={2.8} />Connected
                        </span>
                    </div>
                    <div className="il-mono" style={{ fontSize: 12.5, color: "var(--fg4)", marginTop: 2 }}>
                        {state.siteOrOrg}
                    </div>
                </div>
                <span style={{ fontSize: 12.5, color: "var(--fg3)", flex: "0 0 auto" }}>
                    {legacyProductOnly
                        ? `${productCount} product${productCount === 1 ? "" : "s"} connected`
                        : `${count} target${count === 1 ? "" : "s"} connected`}
                </span>
                <button type="button" onClick={props.onLink ?? props.onLinkProduct} disabled={props.linking}
                    style={{
                        height: 34, padding: "0 13px", borderRadius: 8,
                        border: "1px solid var(--primary)", background: "var(--primary-soft)",
                        color: "var(--primary)", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                        fontFamily: "inherit", flex: "0 0 auto", display: "inline-flex",
                        alignItems: "center", gap: 6, opacity: props.linking ? 0.55 : 1
                    }}>
                    <Plus size={14} strokeWidth={2} />{legacyProductOnly ? "Link Product" : "Link"}
                </button>
                <button type="button" onClick={e => setManageAnchor(e.currentTarget)}
                    style={{
                        height: 34, padding: "0 13px", borderRadius: 8,
                        border: "1px solid var(--border-strong)", background: "var(--bg-paper)",
                        color: "var(--fg2)", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
                        fontFamily: "inherit", flex: "0 0 auto"
                    }}>
                    Manage
                </button>
                <MuiMenu open={Boolean(manageAnchor)} anchorEl={manageAnchor}
                    onClose={() => setManageAnchor(null)}>
                    <MuiMenuItem disabled={count === 0 || hasSyncInProgress}
                        onClick={() => { setManageAnchor(null); props.onManageResync(); }}>
                        {props.resyncing ? "Re-syncing…" : "Re-sync"}
                    </MuiMenuItem>
                    <MuiMenuItem onClick={() => { setManageAnchor(null); props.onManageReconnect(); }}>Reconnect</MuiMenuItem>
                    <MuiMenuItem disabled={count > 0}
                        onClick={() => { setManageAnchor(null); props.onManageDisconnect(); }}>
                        Disconnect
                    </MuiMenuItem>
                </MuiMenu>
                <button type="button"
                    aria-label={`${collapsed ? "Expand" : "Collapse"} ${state.meta.name} ${legacyProductOnly ? "linked products" : "linked targets"}`}
                    onClick={props.onToggleCollapsed}
                    style={{
                        width: 44, height: 44, borderRadius: 9, border: "none", background: "transparent",
                        color: "var(--fg4)", display: "flex", alignItems: "center",
                        justifyContent: "center", cursor: "pointer", flex: "0 0 auto"
                    }}>
                    {collapsed
                        ? <ChevronDown size={17} strokeWidth={1.7} />
                        : <ChevronUp size={17} strokeWidth={1.7} />}
                </button>
            </div>
            <MuiCollapse in={!collapsed} timeout={250} unmountOnExit>
                {state.listingError && (
                    <div className="il-listing-error">
                        <span>{`Couldn't refresh source details: ${state.listingError}`}</span>
                        <button type="button" onClick={props.onRetryListing}>Retry</button>
                    </div>
                )}
                {showProducts && (legacyProductOnly ? (
                    <LinkTable state={state} productNameById={props.productNameById as ReadonlyMap<number, string>}
                        syncingIds={props.syncingIds} pendingUnlinkId={props.pendingUnlinkId}
                        onSync={props.onSync} onUnlink={props.onUnlink} />
                ) : (
                    <TargetSection title="Products" count={productCount}>
                        <LinkTable state={state} targetType="product" targetNameById={props.productNameById as ReadonlyMap<string, string>}
                            syncingIds={props.syncingIds} pendingUnlinkKey={props.pendingUnlinkKey}
                            onSync={props.onSync} onUnlink={props.onUnlink} />
                    </TargetSection>
                ))}
                {showContextMeshes && (
                    <TargetSection title="Context Meshes" count={contextMeshCount}>
                        <LinkTable state={state} targetType="context_mesh" targetNameById={props.contextMeshNameById ?? new Map()}
                            syncingIds={props.syncingIds} pendingUnlinkKey={props.pendingUnlinkKey}
                            onSync={props.onSync} onUnlink={props.onUnlink} />
                    </TargetSection>
                )}
            </MuiCollapse>
        </div>
    );
}

function TargetSection({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
    const [open, setOpen] = useState(true);
    return (
        <section style={{ borderTop: "1px solid var(--divider)" }}>
            <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open}
                style={{ width: "100%", border: 0, background: "var(--bg-paper)", padding: "12px 20px",
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "var(--fg1)",
                    font: "inherit", fontWeight: 600, textAlign: "left" }}>
                {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span>{title}</span>
                <span style={{ color: "var(--fg4)", fontSize: 12, fontWeight: 500 }}>{count}</span>
            </button>
            <MuiCollapse in={open} timeout={180}>{children}</MuiCollapse>
        </section>
    );
}

function NotConnectedCard(props: Props) {
    const { state } = props;
    const field = state.meta.connectField;
    const [showField, setShowField] = useState(false);
    const [value, setValue] = useState("");
    const needsField = Boolean(field);

    const handleConnectClick = () => {
        if (!needsField) {
            props.onConnect("");
            return;
        }
        setShowField(current => !current);
    };

    return (
        <div style={{ ...cardStyle, marginBottom: 10 }}>
            <div style={{ padding: "15px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                <Tile state={state} size={36} />
                <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "var(--fg1)" }}>{state.meta.name}</span>
                    {state.meta.descriptor && (
                        <span style={{ fontSize: 12.5, color: "var(--fg4)", marginLeft: 10 }}>{state.meta.descriptor}</span>
                    )}
                </div>
                <span style={{
                    fontSize: 12, fontWeight: 600, color: "var(--fg4)", background: "var(--bg-subtle)",
                    border: "1px solid var(--border)", padding: "3px 10px", borderRadius: 999
                }}>
                    Not connected
                </span>
                <button type="button" className="il-connect-button" onClick={handleConnectClick}
                    aria-expanded={needsField ? showField : undefined}
                >
                    Connect
                </button>
                {needsField && (
                    <button type="button" aria-label={showField ? "Collapse" : "Expand"}
                        aria-expanded={showField} onClick={handleConnectClick}
                        style={{
                            width: 30, height: 30, borderRadius: 7, border: "none",
                            background: "transparent", color: "var(--fg4)", display: "flex",
                            alignItems: "center", justifyContent: "center", cursor: "pointer"
                        }}>
                        {showField ? <ChevronUp size={17} strokeWidth={1.7} /> : <ChevronDown size={17} strokeWidth={1.7} />}
                    </button>
                )}
            </div>
            {needsField && (
                <MuiCollapse in={showField} timeout={250}>
                    <div style={{
                        padding: "0 20px 16px", display: "flex", alignItems: "center", gap: 12,
                        flexWrap: "wrap"
                    }}>
                        <div style={{ flex: "1 1 300px" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--fg2)", marginBottom: 4 }}>
                                {field!.label}
                            </div>
                            <MuiInputBase fullWidth placeholder={field!.placeholder} value={value}
                                onChange={e => setValue(e.target.value)}
                                sx={{
                                    fontSize: 13, border: "1px solid var(--border-strong)",
                                    borderRadius: "8px", px: 1.25, py: 0.5
                                }} />
                            <div style={{ fontSize: 11.5, color: "var(--fg4)", marginTop: 3 }}>{field!.helperText}</div>
                        </div>
                        <button type="button" className="il-connect-button"
                            disabled={field!.required && !value.trim()}
                            onClick={() => props.onConnect(value.trim())}
                        >
                            Connect with OAuth
                        </button>
                    </div>
                </MuiCollapse>
            )}
        </div>
    );
}

export function ProviderCard(props: Props) {
    return props.state.connected ? <ConnectedCard {...props} /> : <NotConnectedCard {...props} />;
}
